/**
 * engine/subscription.ts — engine subscription + lifecycle quartet
 * (start / abort / restart / reset). Owns the worker-engine instance.
 *
 * The `Speedtest` class is dynamic-imported on first `start()` so the
 * eager bundle doesn't pull engine.ts + ping/download/upload phases +
 * EMA helpers + ring-buffer. First Start pays an async chunk-preload
 * cost; subsequent starts re-use the cached reference.
 *
 * The lifecycle quartet stays sync — `start()` and `restart()` kick off
 * the dynamic-import then return; eventual readiness flows through the
 * reactive `data` / `isRunning` / `results` refs. Fire-and-forget call
 * sites don't need to await.
 */

import { ref, shallowRef, type Ref, type ShallowRef } from "vue";

import type {
    Speedtest as SpeedtestClass,
    SpeedtestConfig,
    SpeedtestResults as EngineResults,
} from "@speedtest/index.ts";

import type { MetricStates } from "../types.ts";
import { resetMetricStates } from "../metric-state.ts";
import type { ProgressTracker } from "./progress.ts";
import { applyFinal, logFinal, submitFinal, type SubmissionContext } from "./submission.ts";

/** Cached dynamic-imported `Speedtest` constructor. The first `start()`
 *  pays the network/parse cost; subsequent calls re-use this reference.
 *  Module-scoped so multiple `createSubscriptionMachine` instances (one
 *  per Pinia store boot) share the same loaded module. */
let SpeedtestCls: typeof SpeedtestClass | null = null;

async function ensureSpeedtestCls(): Promise<typeof SpeedtestClass> {
    if (!SpeedtestCls) {
        const mod = await import("@speedtest/index.ts");
        SpeedtestCls = mod.Speedtest;
    }
    return SpeedtestCls;
}

export interface SubscriptionOptions {
    serverUrl: string;
    metricStates: MetricStates;
    progress: ProgressTracker;
    submission: SubmissionContext;
}

export interface SubscriptionMachine {
    isRunning: Ref<boolean>;
    results: Ref<EngineResults | null>;
    initialize: (onEnd?: (aborted: boolean) => void) => void;
    configure: (overrides: Partial<SpeedtestConfig>) => void;
    start: () => void;
    abort: () => void;
    restart: () => void;
    reset: () => void;
}

export function createSubscriptionMachine(
    options: SubscriptionOptions,
): SubscriptionMachine {
    const { serverUrl, metricStates, progress, submission } = options;

    const speedtest: ShallowRef<SpeedtestClass | null> = shallowRef(null);
    const onEndCallback: ShallowRef<((aborted: boolean) => void) | undefined> =
        shallowRef(undefined);
    const configOverrides: ShallowRef<Partial<SpeedtestConfig>> = shallowRef({});

    const isRunning: Ref<boolean> = ref(false);
    const results: Ref<EngineResults | null> = ref(null);
    /** Latch set inside `abort()` so the rejection branch can
     *  distinguish a user-initiated stop (expected) from an engine /
     *  worker crash (unexpected). Without it, both shapes would surface
     *  an identical `onEnd(true)` and the rejection would silently
     *  swallow — every dev session that hit a worker bug would read
     *  as a normal abort. */
    const abortedByUser: Ref<boolean> = ref(false);

    // Initial-load hygiene: the chassis must read idle on first paint
    // regardless of any future persisted-state plugin or Pinia hydration.
    // Every live-mode flag explicitly resets here at engine construction
    // so the boot path is one-line auditable.
    isRunning.value = false;
    results.value = null;

    function resetState(): void {
        resetMetricStates(metricStates);
        progress.reset();
    }

    function initialize(onEnd?: (aborted: boolean) => void): void {
        onEndCallback.value = onEnd;
    }

    function configure(overrides: Partial<SpeedtestConfig>): void {
        configOverrides.value = { ...configOverrides.value, ...overrides };
    }

    function start(): void {
        // Reset state
        resetState();
        isRunning.value = true;
        results.value = null;
        // Clear the abort latch — a fresh start is not a continuation
        // of a prior abort.
        abortedByUser.value = false;

        // Dynamic-import the engine class + run() chain. The outer
        // `start()` stays sync so fire-and-forget call sites don't need
        // to await; the inner async function awaits the import + run +
        // success/failure routing in one linear flow.
        void runLifecycle();
    }

    async function runLifecycle(): Promise<void> {
        const Cls = await ensureSpeedtestCls();
        // Abort might have fired between `start()` invocation and
        // the dynamic-import resolution; bail if no longer running.
        if (!isRunning.value) return;

        // Create fresh instance per test
        const test = new Cls({
            serverUrl,
            ...configOverrides.value,
        });
        speedtest.value = test;

        test.onProgress = (p) => {
            progress.updateFromProgress(p);
        };

        try {
            const { results: r } = await test.run();
            results.value = r;
            logFinal(r);
            applyFinal(progress, metricStates, r);
            isRunning.value = false;
            // Fire any phase boundaries that haven't fired yet
            // (e.g. when a phase ended on its very last sample
            // without crossing into the next phase's progress
            // event), then the final snapshot.
            submitFinal(submission, r);
            onEndCallback.value?.(false);
        } catch (err: unknown) {
            // Distinguish abort (expected) from engine/worker crash
            // (unexpected). The `abortedByUser` latch is set inside
            // `abort()` so a user-initiated stop reads silently; any
            // other rejection is an unexpected engine failure surfaced
            // through `console.error` so a worker crash is visible to
            // the operator.
            isRunning.value = false;
            if (!abortedByUser.value) {
                console.error("[speedtest] engine rejected", {
                    code: "ENGINE_REJECTED",
                    error: err instanceof Error ? err.message : String(err),
                    stack: err instanceof Error ? err.stack : undefined,
                });
            }
            onEndCallback.value?.(true);
        }
    }

    function abort(): void {
        const hadEngine = speedtest.value != null;
        // Latch the user-initiated abort BEFORE the engine's `.abort()`
        // fires — the engine's `run()` Promise rejects asynchronously
        // and the rejection branch reads this latch to know whether
        // to log a crash or stay silent.
        abortedByUser.value = true;
        speedtest.value?.abort();
        speedtest.value = null;
        isRunning.value = false;
        // Clear the completion latch — an abort isn't a completion. The
        // store-side reset prevents `restart()` (which calls `abort()` then
        // `start()`) from leaving stale `results` visible during the brief
        // window before the next test reaches its first phase. Without this
        // the chassis can read `currentPhase === "complete"` even after a
        // user-driven Stop.
        results.value = null;
        resetState();
        // When abort fires outside of an active test promise (e.g. from
        // `reset()` after a completed run), the test engine's rejection
        // branch never fires, so the onEnd callback never propagates.
        // Fire it explicitly so consumer-registered hooks see the abort.
        if (!hadEngine) {
            onEndCallback.value?.(true);
        }
    }

    function restart(): void {
        abort();
        start();
    }

    function reset(): void {
        abort();
        progress.data.value = null;
        results.value = null;
    }

    return {
        isRunning,
        results,
        initialize,
        configure,
        start,
        abort,
        restart,
        reset,
    };
}
